import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { DialogService } from '@gains-com/ui-angular';
import { RegionRestrictionDialogComponent } from '../../components/region-restriction-dialog/region-restriction-dialog.component.js';
import { DialogKey } from '../../shared/dialog-keys.js';
import { LOGGER_FACTORY } from '../logger/logger-factory.provider.js';
import { MerchDialogService } from '../merch-dialog.service.js';
import { LocationGateViewService } from '../view-states/location-gate-view.service.js';
import { UserViewService } from '../view-states/user-view.service.js';
import { SKIP_REGION_CHECK_INTERCEPTOR } from './skip-region-check-interceptor.js';

// Only the Buy Coins mints: the casino-session, dice, blackjack and packs
// routes return the same code for a different restriction (and packs carries
// no `mode` in its payload) and must not open the shop.
const BUY_COINS_MINT_URL = /(?<!\/packs)\/coinflow\/session-key|\/payment-methods\/fireblocks-purchase-addresses/;

/**
 * HTTP interceptor that handles global error responses. Catches 403
 * REGION_RESTRICTED (region restriction modal), 403 LOCATION_REQUIRED
 * (the re-ask dialog — per-action, no latch) and 403 MODE_RESTRICTED on the
 * Buy Coins mints (merch device — hand off to the shop).
 */
export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const logger = inject(LOGGER_FACTORY).create('HttpError');
  const dialog = inject(DialogService);
  const userView = inject(UserViewService);
  const locationGate = inject(LocationGateViewService);
  const merchDialog = inject(MerchDialogService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (req.context.get(SKIP_REGION_CHECK_INTERCEPTOR)) {
        return next(req); // Skip this interceptor's logic
      }

      if (
        error.status === 403 &&
        error.error?.code === 'REGION_RESTRICTED'
      ) {
        userView.updateUser({
          region_restricted: true
        });

        // Only open the dialog if it's not already open
        const existingDialog = dialog.getDialogRefById(
          DialogKey.RegionRestriction
        );
        if (!existingDialog) {
          dialog.open(RegionRestrictionDialogComponent, {
            id: DialogKey.RegionRestriction,
            disableClose: true
          });
        }
      }

      if (error.status === 403 && error.error?.code === 'LOCATION_REQUIRED') {
        locationGate.handleLocationRequired(req.url);
      }

      if (
        error.status === 403 &&
        error.error?.code === 'MODE_RESTRICTED' &&
        BUY_COINS_MINT_URL.test(req.url)
      ) {
        // The dialog only got this far on a stale `/self` mode; patch it so
        // openShop's own merch check passes without another round-trip.
        const mode = error.error.data?.mode;
        if (mode) {
          userView.updateUser({ device_mode: mode });
        }
        dialog.getDialogRefById(DialogKey.Purchases)?.close();
        void merchDialog.openShop();
      }

      logger.error(error.message, {
        status: error.status,
        response_type: error.responseType,
        method: req.method,
        url: error.url,
        error: error.error
      });

      return throwError(() => error);
    })
  );
};
